import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Truck, User, FileText, CheckCircle, Phone, CreditCard, Package, Building2, Search } from 'lucide-react';
import { SmartDropdown, FilterSection, useToast, LoadingState, EmptyState } from '../components/ui';
import Form103Modal from '../components/Form103Modal';
import api from '../services/api';

const GateEntry = () => {
  const queryClient = useQueryClient();
  const { success, error: showError } = useToast();

  const [formData, setFormData] = useState({
    truckNumber: '',
    driverId: '',
    driverName: '',
    driverLicense: '',
    driverPhone: '',
    supplierId: '',
    poNumber: '',
    lrNumber: '',
    invoiceNumber: '',
    ewayBillNumber: '',
    notes: ''
  });

  // Filters for recent arrivals
  const [filters, setFilters] = useState({
    search: '',
    status: 'all'
  });

  // State for dock assignment
  const [showDockModal, setShowDockModal] = useState(false);
  const [selectedTruckForDock, setSelectedTruckForDock] = useState(null);
  const [autoAssigning, setAutoAssigning] = useState(false);

  // State for Form 103 modal
  const [showForm103Modal, setShowForm103Modal] = useState(false);
  const [selectedTruckForForm103, setSelectedTruckForForm103] = useState(null);

  // Fetch suppliers
  const { data: suppliers = [] } = useQuery({
    queryKey: ['suppliers'],
    queryFn: async () => {
      const response = await api.get('/procurement/suppliers/?is_active=true');
      return response.data.results || response.data;
    }
  });

  // Fetch active purchase orders (excludes fully received POs)
  const { data: purchaseOrders = [] } = useQuery({
    queryKey: ['purchase-orders-active'],
    queryFn: async () => {
      const response = await api.get('/procurement/purchase-orders/active/');
      return response.data.results || response.data;
    }
  });

  // Fetch drivers
  const { data: drivers = [] } = useQuery({
    queryKey: ['drivers'],
    queryFn: async () => {
      const response = await api.get('/gate/drivers/');
      return response.data.results || response.data;
    }
  });

  // Filter purchase orders based on selected supplier
  // Note: po.supplier is the supplier ID (not an object), so we compare directly
  const filteredPurchaseOrders = formData.supplierId
    ? purchaseOrders.filter(po => po.supplier === parseInt(formData.supplierId))
    : purchaseOrders;

  // Clear PO selection when supplier changes
  useEffect(() => {
    if (formData.supplierId && formData.poNumber) {
      // Check if currently selected PO belongs to the new supplier
      const selectedPO = purchaseOrders.find(po => po.id === parseInt(formData.poNumber));
      if (selectedPO && selectedPO.supplier !== parseInt(formData.supplierId)) {
        // Clear PO if it doesn't belong to the selected supplier
        setFormData(prev => ({ ...prev, poNumber: '' }));
      }
    }
  }, [formData.supplierId, formData.poNumber, purchaseOrders]);

  // Fetch recent arrivals with filters
  const { data: recentArrivals = [], isLoading: arrivalsLoading } = useQuery({
    queryKey: ['truck-arrivals', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.status !== 'all') params.append('status', filters.status);
      if (filters.search) params.append('search', filters.search);

      const response = await api.get(`/gate/trucks/?${params.toString()}`);
      return response.data.results || response.data;
    }
  });

  // Fetch today's stats
  const { data: todayStats = { total: 0, pending: 0, processed: 0 } } = useQuery({
    queryKey: ['gate-stats-today'],
    queryFn: async () => {
      const today = new Date().toISOString().split('T')[0];
      const response = await api.get(`/gate/trucks/?arrival_date=${today}`);
      const arrivals = response.data.results || response.data;

      return {
        total: arrivals.length,
        pending: arrivals.filter(a => ['ARRIVED', 'GATE_CHECK'].includes(a.status)).length,
        processed: arrivals.filter(a => !['ARRIVED', 'GATE_CHECK'].includes(a.status)).length
      };
    }
  });

  // Auto-assign dock handler
  const handleAutoAssignDock = async (truckId) => {
    setAutoAssigning(true);
    try {
      const response = await api.post('/warehouse/docks/auto_assign/', {
        truck_id: truckId
      });
      queryClient.invalidateQueries(['truck-arrivals']);
      success(response.data.message || 'Dock auto-assigned successfully!');
      setShowDockModal(false);
      setSelectedTruckForDock(null);
    } catch (err) {
      const errorData = err.response?.data;

      // Check if error is due to missing Form 103
      if (errorData?.required_step === 'create_form_103') {
        showError('Please create Form 103 first');
        setShowDockModal(false);

        // Auto-open Form 103 modal
        const truckData = recentArrivals.find(t => t.id === truckId);
        if (truckData) {
          setSelectedTruckForForm103(truckData);
          setShowForm103Modal(true);
        }
      } else {
        showError(errorData?.error || 'Failed to auto-assign dock');
      }
    } finally {
      setAutoAssigning(false);
    }
  };

  // Form 103 success handler
  const handleForm103Success = (data) => {
    // Invalidate all truck queries to force refresh
    queryClient.invalidateQueries(['truck-arrivals']);
    queryClient.invalidateQueries(['gate-stats-today']);

    // Force immediate refetch
    queryClient.refetchQueries(['truck-arrivals']);

    setShowForm103Modal(false);
    setSelectedTruckForForm103(null);
    success(`Form 103 ${data.summary.grn_number} created successfully!`);
  };

  // Register truck mutation
  const registerTruckMutation = useMutation({
    mutationFn: async (data) => {
      const response = await api.post('/gate/trucks/check-in/', data);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['truck-arrivals']);
      queryClient.invalidateQueries(['gate-stats-today']);
      resetForm();
      success(`Truck ${data.truck_number} registered successfully!`);
    },
    onError: (err) => {
      console.error('Gate entry error:', err.response || err);
      console.error('Error response data:', err.response?.data);

      // Try to extract the most relevant error message
      const errorData = err.response?.data;
      let errorMessage = 'Failed to register truck';

      if (typeof errorData === 'string') {
        errorMessage = errorData;
      } else if (errorData) {
        // Check for common error fields
        errorMessage = errorData.detail
          || errorData.error
          || errorData.driver_license?.[0]
          || errorData.supplier_code?.[0]
          || errorData.truck_number?.[0]
          || errorData.po_number?.[0]
          || JSON.stringify(errorData);
      }

      showError(errorMessage);
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();

    // Get selected driver's license number
    const selectedDriver = drivers.find(d => d.id === parseInt(formData.driverId));
    const driverLicense = selectedDriver ? selectedDriver.license_number : formData.driverLicense;

    // Get selected supplier's code
    const selectedSupplier = suppliers.find(s => s.id === parseInt(formData.supplierId));
    const supplierCode = selectedSupplier ? selectedSupplier.code : '';

    if (!driverLicense) {
      showError('Driver license is required');
      return;
    }

    if (!supplierCode) {
      showError('Supplier is required');
      return;
    }

    // Get selected PO number
    const selectedPO = purchaseOrders.find(po => po.id === parseInt(formData.poNumber));
    const poNumber = selectedPO ? selectedPO.po_number : '';

    const registrationData = {
      truck_number: formData.truckNumber,
      driver_license: driverLicense,
      supplier_code: supplierCode,
      po_number: poNumber || '',
      lr_number: formData.lrNumber,
      invoice_number: formData.invoiceNumber,
      eway_bill_number: formData.ewayBillNumber || '',
      notes: formData.notes
    };

    console.log('Submitting registration data:', registrationData);
    registerTruckMutation.mutate(registrationData);
  };

  const resetForm = () => {
    setFormData({
      truckNumber: '',
      driverId: '',
      driverName: '',
      driverLicense: '',
      driverPhone: '',
      supplierId: '',
      poNumber: '',
      lrNumber: '',
      invoiceNumber: '',
      ewayBillNumber: '',
      notes: ''
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Handle supplier change - clear PO when supplier changes
  const handleSupplierChange = (e) => {
    const { name, value } = e.target;

    // If changing supplier and a PO is selected, clear it
    if (name === 'supplierId' && formData.poNumber) {
      const selectedPO = purchaseOrders.find(po => po.id === parseInt(formData.poNumber));
      if (selectedPO && selectedPO.supplier !== parseInt(value)) {
        setFormData({
          ...formData,
          supplierId: value,
          poNumber: ''  // Clear PO
        });
        return;
      }
    }

    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Handle driver selection - populate driver details
  const handleDriverChange = (e) => {
    const driverId = e.target.value;
    setFormData({ ...formData, driverId });

    if (driverId) {
      const selectedDriver = drivers.find(d => d.id === parseInt(driverId));
      if (selectedDriver) {
        setFormData(prev => ({
          ...prev,
          driverId,
          driverName: selectedDriver.name,
          driverLicense: selectedDriver.license_number || '',
          driverPhone: selectedDriver.phone || ''
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        driverId: '',
        driverName: '',
        driverLicense: '',
        driverPhone: ''
      }));
    }
  };

  // Handle filter changes
  const handleFilterChange = (name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      status: 'all'
    });
  };

  // Status badge helper
  const getStatusBadge = (status) => {
    const statusConfig = {
      'ARRIVED': { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Arrived' },
      'GATE_CHECK': { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Gate Check' },
      'DOCUMENTS_VERIFIED': { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Verified' },
      'DOCK_ASSIGNED': { bg: 'bg-indigo-100', text: 'text-indigo-800', label: 'At Dock' },
      'UNLOADING': { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Unloading' },
      'QC_PENDING': { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pending QC' },
      'COMPLETED': { bg: 'bg-green-100', text: 'text-green-800', label: 'Completed' },
      'SAFETY_CHECK_FAILED': { bg: 'bg-red-100', text: 'text-red-800', label: 'Failed' },
    };

    const config = statusConfig[status] || { bg: 'bg-gray-100', text: 'text-gray-800', label: status };
    return (
      <span className={`text-xs px-2 py-1 rounded ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  // Filter options
  const filterOptions = [
    {
      name: 'status',
      label: 'Status',
      value: filters.status,
      options: [
        { value: 'ARRIVED', label: 'Arrived' },
        { value: 'GATE_CHECK', label: 'Gate Check' },
        { value: 'DOCUMENTS_VERIFIED', label: 'Verified' },
        { value: 'DOCK_ASSIGNED', label: 'At Dock' },
        { value: 'UNLOADING', label: 'Unloading' },
        { value: 'QC_PENDING', label: 'Pending QC' },
        { value: 'COMPLETED', label: 'Completed' }
      ]
    }
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Gate Entry</h1>
        <p className="text-gray-600 mt-2">Register incoming trucks and manage gate operations</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Registration Form */}
        <div className="lg:col-span-2">
          <div className="card">
            <div className="flex items-center mb-6">
              <Truck className="w-6 h-6 text-nexgensis-primary mr-2" />
              <h2 className="text-xl font-semibold">Truck Registration</h2>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Truck Number */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Truck Number <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Truck className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      name="truckNumber"
                      value={formData.truckNumber}
                      onChange={handleChange}
                      className="input-field pl-10"
                      placeholder="MH-12-AB-1234"
                      required
                    />
                  </div>
                </div>

                {/* Driver Dropdown */}
                <SmartDropdown
                  label="Driver"
                  name="driverId"
                  value={formData.driverId}
                  onChange={handleDriverChange}
                  options={[
                    { value: 'new', label: '+ Add New Driver' },
                    ...drivers.map(driver => ({
                      value: driver.id,
                      label: `${driver.name} - ${driver.license_number || 'No License'}`
                    }))
                  ]}
                  icon={User}
                  placeholder="Select driver or add new"
                />

                {/* Driver Name (auto-populated or manual) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Driver Name {!formData.driverId && <span className="text-red-500">*</span>}
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      name="driverName"
                      value={formData.driverName}
                      onChange={handleChange}
                      className="input-field pl-10"
                      placeholder="Enter driver name"
                      required={!formData.driverId}
                      disabled={formData.driverId && formData.driverId !== 'new'}
                    />
                  </div>
                </div>

                {/* Driver License */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Driver License
                  </label>
                  <div className="relative">
                    <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      name="driverLicense"
                      value={formData.driverLicense}
                      onChange={handleChange}
                      className="input-field pl-10"
                      placeholder="DL-XXXXXXXXXX"
                      disabled={formData.driverId && formData.driverId !== 'new'}
                    />
                  </div>
                </div>

                {/* Driver Phone */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Driver Phone
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="tel"
                      name="driverPhone"
                      value={formData.driverPhone}
                      onChange={handleChange}
                      className="input-field pl-10"
                      placeholder="+91 XXXXXXXXXX"
                      disabled={formData.driverId && formData.driverId !== 'new'}
                    />
                  </div>
                </div>

                {/* Supplier Dropdown - Searchable */}
                <SmartDropdown
                  label="Supplier Name"
                  name="supplierId"
                  value={formData.supplierId}
                  onChange={handleSupplierChange}
                  options={suppliers.map(supplier => ({
                    value: supplier.id,
                    label: `${supplier.code} - ${supplier.name}`
                  }))}
                  icon={Building2}
                  required
                  placeholder="Search supplier..."
                  searchable={true}
                />

                {/* PO Number Dropdown */}
                <SmartDropdown
                  label="PO Number"
                  name="poNumber"
                  value={formData.poNumber}
                  onChange={handleChange}
                  options={filteredPurchaseOrders.map(po => ({
                    value: po.id,
                    label: `${po.po_number} - ${po.supplier?.name || ''}`
                  }))}
                  icon={FileText}
                  placeholder={formData.supplierId ? "Select PO (optional)" : "Select supplier first"}
                  helperText={
                    formData.supplierId
                      ? `${filteredPurchaseOrders.length} PO(s) available for selected supplier. Materials will be fetched during unloading.`
                      : "Please select a supplier to view available POs"
                  }
                  disabled={!formData.supplierId}
                />

                {/* LR Number */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    LR Number
                  </label>
                  <input
                    type="text"
                    name="lrNumber"
                    value={formData.lrNumber}
                    onChange={handleChange}
                    className="input-field"
                    placeholder="Lorry Receipt Number"
                  />
                </div>

                {/* Invoice Number */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Invoice Number
                  </label>
                  <input
                    type="text"
                    name="invoiceNumber"
                    value={formData.invoiceNumber}
                    onChange={handleChange}
                    className="input-field"
                    placeholder="Invoice/Challan Number"
                  />
                </div>

                {/* E-way Bill Number */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    E-way Bill Number
                  </label>
                  <input
                    type="text"
                    name="ewayBillNumber"
                    value={formData.ewayBillNumber}
                    onChange={handleChange}
                    className="input-field"
                    placeholder="E-way Bill Number (Optional)"
                  />
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  className="input-field"
                  rows="3"
                  placeholder="Additional notes or special instructions..."
                />
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={resetForm}
                  disabled={registerTruckMutation.isLoading}
                >
                  Clear
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={registerTruckMutation.isLoading}
                >
                  {registerTruckMutation.isLoading ? 'Registering...' : 'Register Truck'}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Recent Arrivals & Stats */}
        <div className="lg:col-span-1 space-y-6">
          {/* Filters */}
          <div className="card">
            <h3 className="font-semibold mb-3 flex items-center">
              <Search className="w-4 h-4 mr-2" />
              Search & Filter
            </h3>
            <div className="space-y-3">
              <input
                type="search"
                placeholder="Search by truck, driver..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="input-field"
              />
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="input-field"
              >
                <option value="all">All Status</option>
                {filterOptions[0].options.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              {(filters.search || filters.status !== 'all') && (
                <button
                  onClick={handleClearFilters}
                  className="text-sm text-blue-600 hover:text-blue-700 w-full text-left"
                >
                  Clear filters
                </button>
              )}
            </div>
          </div>

          {/* Recent Arrivals */}
          <div className="card">
            <div className="flex items-center mb-4">
              <FileText className="w-5 h-5 text-nexgensis-primary mr-2" />
              <h2 className="text-lg font-semibold">Recent Arrivals</h2>
            </div>

            {arrivalsLoading ? (
              <LoadingState message="Loading arrivals..." />
            ) : recentArrivals.length === 0 ? (
              <EmptyState
                icon={Truck}
                title="No arrivals found"
                message={filters.search || filters.status !== 'all'
                  ? "Try adjusting your filters"
                  : "No trucks registered yet"}
              />
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {recentArrivals.slice(0, 10).map((arrival) => (
                  <div key={arrival.id} className="border-l-4 border-nexgensis-primary bg-gray-50 p-3 rounded animate-fade-in">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="font-semibold text-sm">{arrival.truck_number}</p>
                        <p className="text-xs text-gray-600 mt-1">
                          <User className="w-3 h-3 inline mr-1" />
                          {arrival.driver?.name || arrival.driver_name || 'Unknown Driver'}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(arrival.arrival_time || arrival.created_at).toLocaleTimeString('en-IN', {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                        {arrival.status === 'DOCUMENTS_VERIFIED' && (
                          <>
                            {!arrival.has_form_103 ? (
                              // Show Create Form 103 button when Form 103 not created
                              <>
                                <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs">
                                  <p className="text-yellow-800 font-medium">Form 103 (GRN) required</p>
                                </div>
                                <button
                                  onClick={() => {
                                    setSelectedTruckForForm103(arrival);
                                    setShowForm103Modal(true);
                                  }}
                                  className="mt-2 bg-blue-500 text-white px-3 py-1 rounded text-xs hover:bg-blue-600 flex items-center w-full justify-center"
                                >
                                  <FileText className="w-3 h-3 mr-1" />
                                  Create Form 103 (Invoice Entry)
                                </button>
                              </>
                            ) : (
                              // Show Form 103 created badge and Assign Dock button
                              <>
                                <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded">
                                  <p className="text-xs text-green-800 font-medium flex items-center">
                                    <CheckCircle className="w-3 h-3 mr-1" />
                                    Form 103: {arrival.form_103_grn_number}
                                  </p>
                                </div>
                                <button
                                  onClick={() => {
                                    setSelectedTruckForDock(arrival);
                                    setShowDockModal(true);
                                  }}
                                  className="mt-2 bg-purple-500 text-white px-3 py-1 rounded text-xs hover:bg-purple-600 flex items-center w-full justify-center"
                                >
                                  <Package className="w-3 h-3 mr-1" />
                                  Assign Dock
                                </button>
                              </>
                            )}
                          </>
                        )}
                        {arrival.status === 'DOCK_ASSIGNED' && arrival.dock && (
                          <p className="mt-2 text-xs text-green-600 font-medium flex items-center">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Assigned to {arrival.dock.dock_number || `Dock ${arrival.dock.id}`}
                          </p>
                        )}
                      </div>
                      <div>
                        {getStatusBadge(arrival.status)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Quick Stats */}
          <div className="card">
            <h3 className="font-semibold mb-4">Today's Summary</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Total Arrivals</span>
                <span className="font-bold text-lg">{todayStats.total}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Pending</span>
                <span className="font-bold text-lg text-yellow-600">{todayStats.pending}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Processed</span>
                <span className="font-bold text-lg text-green-600">{todayStats.processed}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Dock Assignment Modal */}
      {showDockModal && selectedTruckForDock && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full animate-scale-in">
            <h2 className="text-2xl font-bold mb-4">🤖 Assign Dock</h2>

            <div className="mb-6 bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">Truck Number</p>
              <p className="text-lg font-bold">{selectedTruckForDock.truck_number}</p>
              <p className="text-sm text-gray-600 mt-2">Driver</p>
              <p className="text-md">{selectedTruckForDock.driver?.name || selectedTruckForDock.driver_name || 'Unknown'}</p>
              <p className="text-sm text-gray-600 mt-2">Supplier</p>
              <p className="text-md">{selectedTruckForDock.supplier?.name || 'Unknown'}</p>
            </div>

            <div className="space-y-3">
              <button
                onClick={() => handleAutoAssignDock(selectedTruckForDock.id)}
                disabled={autoAssigning}
                className="w-full bg-gradient-to-r from-purple-500 to-purple-600 text-white px-4 py-3 rounded-lg hover:from-purple-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {autoAssigning ? (
                  <>
                    <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Auto-Assigning...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5 mr-2" />
                    🤖 Auto-Assign Best Dock (AI Recommended)
                  </>
                )}
              </button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">or</span>
                </div>
              </div>

              <button
                onClick={() => {
                  setShowDockModal(false);
                  // Redirect to Dock Management for manual selection
                  window.location.href = '/dock-management';
                }}
                className="w-full bg-blue-500 text-white px-4 py-3 rounded-lg hover:bg-blue-600 flex items-center justify-center"
              >
                <Package className="w-5 h-5 mr-2" />
                Manual Dock Selection
              </button>

              <button
                onClick={() => {
                  setShowDockModal(false);
                  setSelectedTruckForDock(null);
                }}
                className="w-full bg-gray-200 text-gray-800 px-4 py-3 rounded-lg hover:bg-gray-300"
                disabled={autoAssigning}
              >
                Cancel
              </button>
            </div>

            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
              <p className="text-xs text-blue-800">
                💡 <strong>AI Auto-Assign</strong> uses intelligent scoring based on:
                <br/>• Material type compatibility
                <br/>• Historical dock performance
                <br/>• Current availability & capacity
                <br/>• Required facilities (weighbridge, forklift)
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Form 103 Modal */}
      <Form103Modal
        truck={selectedTruckForForm103}
        isOpen={showForm103Modal}
        onClose={() => {
          setShowForm103Modal(false);
          setSelectedTruckForForm103(null);
        }}
        onSuccess={handleForm103Success}
      />
    </div>
  );
};

export default GateEntry;
